import {
  FaceLandmarksDetector,
  Keypoint,
} from "@tensorflow-models/face-landmarks-detection";
import { ImageWithLandMark, PointLike } from "./image-with-landmark";
import { AttentionService } from "../attention.service";

export interface AttentionOptions {
  batch?: number;
  timePerBatch?: number;
  totalTime?: number;
}

export async function batchDetectLandmarks(
  images: HTMLImageElement[],
  options?: AttentionOptions
): Promise<ImageWithLandMark[]> {
  const count = options?.batch ?? images.length;
  const totalTime = options?.totalTime ?? 0;
  const timePerBatch = options?.timePerBatch ?? totalTime / count;

  return doInBatch(images, timePerBatch, count);
}

async function doInBatch(
  images: HTMLImageElement[],
  timePerBatch: number,
  count: number
) {
  const partialResults: ImageWithLandMark[][] = [];
  let currentSize = 0;
  while (currentSize < images.length && (await promiseSleep(timePerBatch))) {
    const slice = images.slice(currentSize, currentSize + count);
    currentSize += count;
    partialResults.push(await detectLandmarks(slice));
  }

  const totalResults = partialResults.reduce((a, b) => {
    for (const iterator of b) {
      a.push(iterator);
    }

    return a;
  });

  return totalResults;
}

function promiseSleep(milisseconds: number) {
  return new Promise((rs) => {
    setTimeout(() => {
      rs(true);
    }, milisseconds);
  });
}

export async function detectLandmarks(
  images: HTMLImageElement[]
): Promise<ImageWithLandMark[]> {
  const results: any = [];
  for (const iterator of images) {
    results.push(await detectLandmarkFromImage(iterator));
  }

  return results;
}

async function detectLandmarkFromImage(
  image: HTMLImageElement
): Promise<ImageWithLandMark> {
  const detector = await getDetector();
  const faces = await detector.estimateFaces(image);
  // Force First Face
  const face0 = faces[0];

  if (face0) {
    const landmarks = extract68Landmakrs(face0.keypoints);
    return new ImageWithLandMark(image, landmarks);
  }

  return new ImageWithLandMark(image);
}

function extract68Landmakrs(point: Keypoint[]): PointLike[] {
  const landmark_points_68 = [
    162, 234, 93, 58, 172, 136, 149, 148, 152, 377, 378, 365, 397, 288, 323,
    454, 389, 71, 63, 105, 66, 107, 336, 296, 334, 293, 301, 168, 197, 5, 4, 75,
    97, 2, 326, 305, 33, 160, 158, 133, 153, 144, 362, 385, 387, 263, 373, 380,
    61, 39, 37, 0, 267, 269, 291, 405, 314, 17, 84, 181, 78, 82, 13, 312, 308,
    317, 14, 87,
  ];

  return landmark_points_68.map((e) => ({ _x: point[e].x, _y: point[e].y }));
}

let modelLoaded = false;
let _detector: FaceLandmarksDetector;
export async function getDetector() {
  if (modelLoaded) {
    return _detector;
  }

  modelLoaded = true;

  AttentionService.detectorFaces.subscribe((response) => {
    _detector = response;
  });

  return _detector;
}
